import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { X, HelpCircle, ChevronDown, ChevronRight, Download } from 'lucide-react';
import DownloadPDFButton from './DownloadPDFButton';

/**
 * AdvisoryTeamHelpModal - Displays comprehensive guidance on professional advisors for business owners
 * Includes essential criteria, experience requirements, and questions to ask potential advisors
 */
const AdvisoryTeamHelpModal = ({ isOpen, onClose }) => {
  const [expandedAdvisor, setExpandedAdvisor] = useState(null);
  const [showLessCommon, setShowLessCommon] = useState(false);

  if (!isOpen) return null;

  const toggleAdvisor = (advisorKey) => {
    setExpandedAdvisor(expandedAdvisor === advisorKey ? null : advisorKey);
  };

  // Comprehensive advisor information for common advisors
  const commonAdvisors = [
    {
      key: 'attorney',
      title: 'Business Attorney',
      description: 'A business attorney provides legal guidance on corporate structure, contracts, compliance, and risk mitigation.',
      essentialExperience: [
        'Experience with business transactions, M&A deals, and exit planning',
        'Knowledge of corporate law, securities regulations, and business entity structures',
        'Track record of representing business owners through exits or sales',
        'Understanding of employment law, intellectual property, and contract negotiation'
      ],
      knowledgeAreas: [
        'Entity formation and corporate governance',
        'Contract drafting and negotiation',
        'Merger and acquisition transactions',
        'Intellectual property protection',
        'Employment and labor law compliance',
        'Commercial real estate transactions',
        'Risk management and liability protection'
      ],
      questionsToAsk: [
        'How many business exits or sales have you guided clients through?',
        'What is your experience with businesses in my industry?',
        'Do you have M&A transaction experience, or will you refer that work?',
        'Can you provide references from business owner clients?',
        'What is your fee structure - hourly, flat fee, or retainer?',
        'Who will actually handle my work - you or an associate?',
        'How quickly do you typically respond to urgent matters?',
        'What proactive legal strategies do you recommend for business owners planning an exit?'
      ],
      redFlags: [
        'Primarily handles personal injury or criminal law with limited business experience',
        'Cannot provide references from business owner clients',
        'Unclear or evasive about M&A experience',
        'Reluctant to discuss fee structures upfront',
        'Never asks about your business goals or exit timeline'
      ]
    },
    {
      key: 'accountant',
      title: 'CPA / Accountant',
      description: 'A qualified CPA manages your financial records, tax strategy, and provides critical financial insights for exit planning.',
      essentialExperience: [
        'CPA certification with active license in good standing',
        'Experience working with businesses in your size range and industry',
        'Track record of tax planning strategies that optimize business value',
        'Knowledge of financial statement preparation for due diligence',
        'Experience with quality of earnings reports and transaction support'
      ],
      knowledgeAreas: [
        'Tax planning and compliance (federal, state, and local)',
        'Financial statement preparation and analysis',
        'Entity structure optimization for tax efficiency',
        'Quality of earnings and EBITDA adjustments',
        'Transaction advisory and due diligence support',
        'Cash flow management and forecasting',
        'Internal controls and accounting systems'
      ],
      questionsToAsk: [
        'How many clients have you supported through a business sale or exit?',
        'What is your approach to proactive tax planning vs. compliance-only work?',
        'Can you help prepare our financials for buyer due diligence?',
        'What strategies do you recommend to maximize after-tax proceeds from a sale?',
        'How do you charge - monthly retainer, hourly, or project-based?',
        'What accounting software do you recommend and support?',
        'How often will we meet to review financials and discuss strategy?',
        'Do you work with a transaction advisory team, or can you refer specialists?'
      ],
      redFlags: [
        'Only provides annual tax return preparation without ongoing planning',
        'Cannot articulate strategies for maximizing business value',
        'Unfamiliar with quality of earnings or EBITDA adjustments',
        'Does not ask about your business goals or exit plans',
        'Slow to respond or return calls during critical periods'
      ]
    },
    {
      key: 'financial_advisor',
      title: 'Financial Advisor / Wealth Manager',
      description: 'A financial advisor helps you plan for personal financial goals, investment strategy, and wealth preservation before and after an exit.',
      essentialExperience: [
        'CFP (Certified Financial Planner) or similar credential',
        'Experience working with business owners who have sold companies',
        'Knowledge of concentrated wealth strategies and exit planning',
        'Understanding of investment management and risk mitigation',
        'Ability to coordinate with tax and legal advisors'
      ],
      knowledgeAreas: [
        'Comprehensive financial planning and goal setting',
        'Investment portfolio management and asset allocation',
        'Retirement income planning and cash flow modeling',
        'Estate planning integration and wealth transfer strategies',
        'Risk management and insurance planning',
        'Tax-efficient investment strategies',
        'Liquidity event planning and windfall management'
      ],
      questionsToAsk: [
        'How many business owners have you worked with through a sale or exit?',
        'What is your approach to managing concentrated wealth from a business sale?',
        'Are you a fiduciary, and how are you compensated?',
        'How do you coordinate with my CPA and attorney?',
        'What is your investment philosophy and approach to risk management?',
        'Can you help model different exit scenarios and their financial impact?',
        'Do you provide comprehensive financial planning beyond investment management?',
        'What happens to my wealth if something happens to you or your firm?'
      ],
      redFlags: [
        'Primarily focused on selling investment products rather than planning',
        'Cannot articulate experience with liquidity events or business owners',
        'Unwilling to work collaboratively with other advisors',
        'Not registered as a fiduciary or unclear about compensation',
        'Promises unrealistic investment returns or "guaranteed" strategies'
      ]
    },
    {
      key: 'exit_advisor',
      title: 'Exit / M&A Advisor',
      description: 'An exit or M&A advisor specializes in preparing businesses for sale and guiding owners through the transaction process.',
      essentialExperience: [
        'Track record of completed transactions in your industry and business size',
        'Knowledge of valuation methodologies and market conditions',
        'Experience managing the entire transaction process from preparation to closing',
        'Network of qualified buyers and deal professionals',
        'Understanding of deal structure, negotiation, and due diligence'
      ],
      knowledgeAreas: [
        'Business valuation and market positioning',
        'Buyer identification and qualification',
        'Deal structure and negotiation strategies',
        'Due diligence process management',
        'Transaction documentation and closing procedures',
        'Marketing and positioning businesses for sale',
        'Post-closing transition planning'
      ],
      questionsToAsk: [
        'How many businesses have you sold in my industry and size range?',
        'What is your average time to close and success rate?',
        'How do you value businesses, and what valuation do you expect for mine?',
        'What is your fee structure - success fee percentage, retainer, or both?',
        'Can you provide references from recent clients?',
        'How do you identify and qualify potential buyers?',
        'What preparation work do you recommend before going to market?',
        'How involved will you be in negotiations and due diligence?'
      ],
      redFlags: [
        'Cannot provide verifiable references from recent transactions',
        'Overpromises on valuation without thorough analysis',
        'Charges only hourly or large upfront fees with no success-based component',
        'Limited experience in your industry or business size',
        'Poor communication or unavailability during critical process stages'
      ]
    },
    {
      key: 'insurance_agent',
      title: 'Insurance Agent / Risk Advisor',
      description: 'A risk advisor evaluates business and personal insurance needs to protect value and ensure adequate coverage for potential liabilities.',
      essentialExperience: [
        'Experience with commercial insurance and business risk assessment',
        'Knowledge of liability coverage, key person insurance, and business continuity',
        'Understanding of personal insurance needs for high-net-worth individuals',
        'Ability to review and compare policies from multiple carriers',
        'Track record of handling claims and advocating for clients'
      ],
      knowledgeAreas: [
        'Commercial general liability insurance',
        'Professional liability and errors & omissions coverage',
        'Directors and officers (D&O) insurance',
        'Key person and buy-sell agreement insurance',
        'Property and casualty coverage',
        'Cyber liability and data breach insurance',
        'Umbrella and excess liability policies',
        'Personal insurance for business owners (life, disability, long-term care)'
      ],
      questionsToAsk: [
        'What types of businesses do you primarily work with?',
        'How do you assess risk and determine appropriate coverage levels?',
        'Do you work with multiple carriers, or are you captive to one company?',
        'What is your process for handling claims?',
        'How often do you review and update coverage?',
        'What insurance should I have in place before selling my business?',
        'Do you coordinate with my other advisors on insurance strategy?',
        'What are the most common coverage gaps you see in businesses like mine?'
      ],
      redFlags: [
        'Only sells products from one insurance company',
        'Pushes products without understanding your business and risks',
        'Cannot explain policy exclusions and limitations clearly',
        'Does not conduct regular coverage reviews',
        'Unresponsive when claims need to be filed'
      ]
    },
    {
      key: 'banker',
      title: 'Banker / Lender',
      description: 'A banker provides financing solutions, manages banking relationships, and may offer treasury management and investment banking services.',
      essentialExperience: [
        'Experience working with businesses in your industry and size',
        'Knowledge of various lending products and financing structures',
        'Relationship management and proactive communication',
        'Understanding of working capital needs and business growth financing',
        'Access to specialized services like treasury management or investment banking'
      ],
      knowledgeAreas: [
        'Commercial lending and credit analysis',
        'Lines of credit and term loans',
        'Equipment financing and SBA loans',
        'Cash management and treasury services',
        'Merchant services and payment processing',
        'Foreign exchange and international banking',
        'Investment banking and capital raising (if applicable)'
      ],
      questionsToAsk: [
        'What types of businesses do you typically work with?',
        'What lending products and services does your bank offer?',
        'How do you approach covenant structures and financial requirements?',
        'What is your credit approval process and turnaround time?',
        'Do you offer treasury management and cash flow optimization services?',
        'How can you support our business as we prepare for an exit?',
        'What are your typical loan terms, rates, and fees?',
        'Who will be our primary contact, and how accessible will you be?'
      ],
      redFlags: [
        'Only interested in transactional relationships, not advisory partnerships',
        'Inflexible on lending terms without understanding business context',
        'Poor communication or slow response to inquiries',
        'Cannot explain credit terms or covenants clearly',
        'Frequent turnover in relationship managers'
      ]
    },
    {
      key: 'estate_planner',
      title: 'Estate Planner / Tax Attorney',
      description: 'An estate planner or tax attorney helps structure wealth transfer, minimize estate taxes, and ensure assets are distributed according to your wishes.',
      essentialExperience: [
        'Specialization in estate planning and/or tax law',
        'Experience with business succession and ownership transfers',
        'Knowledge of trusts, gifting strategies, and estate tax mitigation',
        'Understanding of charitable planning and philanthropic structures',
        'Ability to coordinate with financial advisors and CPAs'
      ],
      knowledgeAreas: [
        'Wills, trusts, and powers of attorney',
        'Estate and gift tax planning',
        'Business succession planning',
        'Charitable planning and donor-advised funds',
        'Asset protection strategies',
        'Generation-skipping transfer strategies',
        'Trust administration and probate'
      ],
      questionsToAsk: [
        'What percentage of your practice is dedicated to estate planning?',
        'How many business owners have you helped with succession planning?',
        'What strategies do you recommend for minimizing estate taxes?',
        'How do you integrate business value into an overall estate plan?',
        'What is your approach to protecting assets from creditors and lawsuits?',
        'How do you coordinate with my CPA and financial advisor?',
        'What are your fees for estate planning services?',
        'How often should we review and update our estate plan?'
      ],
      redFlags: [
        'Estate planning is not their primary practice area',
        'Uses outdated or overly complex strategies without clear explanation',
        'Unwilling to coordinate with other advisors',
        'Pushes insurance products as primary estate planning solution',
        'Cannot explain recommendations in plain language'
      ]
    },
    {
      key: 'business_coach',
      title: 'Business Coach / Consultant',
      description: 'A business coach or consultant provides strategic guidance, accountability, and expertise to help you improve operations and prepare for an exit.',
      essentialExperience: [
        'Track record of working with businesses in your industry or stage',
        'Demonstrated expertise in areas critical to your business (operations, sales, marketing, etc.)',
        'Experience preparing businesses for sale or transition',
        'Strong communication and coaching skills',
        'Results-oriented with measurable outcomes'
      ],
      knowledgeAreas: [
        'Strategic planning and business model development',
        'Operational efficiency and process improvement',
        'Sales and marketing strategy',
        'Leadership development and team building',
        'Financial management and profitability improvement',
        'Exit readiness and value acceleration',
        'Change management and organizational development'
      ],
      questionsToAsk: [
        'What is your background and expertise in my industry?',
        'Can you provide case studies or references from similar businesses?',
        'What is your coaching methodology and approach?',
        'How do you measure success and track progress?',
        'What is your fee structure and engagement length?',
        'How often will we meet, and what is your availability between sessions?',
        'Have you helped businesses prepare for and complete exits?',
        'What specific areas do you believe we should focus on first?'
      ],
      redFlags: [
        'Promises quick fixes or unrealistic results',
        'Vague or unverifiable track record',
        'No clear methodology or framework',
        'Poor listening skills or prescriptive approach without understanding your business',
        'Cannot articulate measurable outcomes or success metrics'
      ]
    }
  ];

  // Less common but potentially valuable advisors
  const lessCommonAdvisors = [
    {
      title: 'Fractional CFO',
      description: 'Part-time financial executive who provides high-level financial strategy, reporting, and leadership without the cost of a full-time CFO.',
      whenNeeded: 'When you need strategic financial leadership but cannot justify a full-time CFO. Essential for businesses preparing for sale to ensure financials are investor-ready.'
    },
    {
      title: 'Investment Banker',
      description: 'Specialized advisor for larger transactions (typically $10M+) who manages the sale process, identifies strategic buyers, and maximizes valuation.',
      whenNeeded: 'For businesses with enterprise value above $10-20M or complex transactions requiring institutional buyer networks and sophisticated deal structures.'
    },
    {
      title: 'Valuation Expert',
      description: 'Professional who provides formal business valuations for tax, transaction, or strategic planning purposes.',
      whenNeeded: 'When you need a defensible valuation for estate planning, gift tax purposes, buy-sell agreements, or initial exit planning discussions.'
    },
    {
      title: 'HR Consultant / Employment Attorney',
      description: 'Specialist in employment law, HR compliance, and workforce management.',
      whenNeeded: 'When facing employee relations issues, preparing employee handbooks, or ensuring compliance with employment laws before a transaction.'
    },
    {
      title: 'Cybersecurity Consultant',
      description: 'Expert in protecting digital assets, data security, and compliance with privacy regulations.',
      whenNeeded: 'For businesses handling sensitive customer data or when buyers require evidence of robust cybersecurity practices during due diligence.'
    },
    {
      title: 'Intellectual Property Attorney',
      description: 'Specialist in patents, trademarks, copyrights, and trade secret protection.',
      whenNeeded: 'When IP is a significant component of business value, or when protecting proprietary technology, brands, or creative works.'
    },
    {
      title: 'Supply Chain Consultant',
      description: 'Expert in optimizing procurement, logistics, inventory management, and vendor relationships.',
      whenNeeded: 'For manufacturing, distribution, or retail businesses where supply chain efficiency significantly impacts profitability and valuation.'
    },
    {
      title: 'Marketing Strategist / Agency',
      description: 'Professional who develops and executes marketing strategies to drive growth and build brand value.',
      whenNeeded: 'When revenue growth has stalled, digital presence is weak, or marketing lacks strategic direction. Critical for businesses targeting revenue growth before exit.'
    },
    {
      title: 'Operations Consultant',
      description: 'Specialist in process improvement, efficiency optimization, and operational excellence.',
      whenNeeded: 'When operations are chaotic, margins are compressed, or buyers are likely to scrutinize operational efficiency during due diligence.'
    },
    {
      title: 'Quality of Earnings (QofE) Provider',
      description: 'Accounting firm that provides independent analysis of earnings quality and financial adjustments for transactions.',
      whenNeeded: 'Typically engaged by buyers during due diligence, but sellers can benefit from a pre-transaction QofE to identify and address issues early.'
    },
    {
      title: 'Environmental Consultant',
      description: 'Expert in environmental compliance, site assessments, and regulatory requirements.',
      whenNeeded: 'For businesses in manufacturing, real estate, or industries with environmental risk. Required for Phase I and Phase II environmental assessments in most transactions.'
    },
    {
      title: 'International Tax Specialist',
      description: 'CPA or attorney with expertise in cross-border tax issues and international business structures.',
      whenNeeded: 'For businesses with international operations, foreign ownership, or buyers/sellers outside the United States.'
    }
  ];

  const AdvisorCard = ({ advisor }) => {
    const isExpanded = expandedAdvisor === advisor.key;

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
        <button
          onClick={() => toggleAdvisor(advisor.key)}
          className="w-full px-4 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 flex items-center justify-between transition-colors"
        >
          <h4 className="font-bold text-teal-900 text-left">{advisor.title}</h4>
          {isExpanded ? <ChevronDown size={20} className="text-teal-600" /> : <ChevronRight size={20} className="text-teal-600" />}
        </button>

        {isExpanded && (
          <div className="p-4 space-y-4 bg-white">
            <div>
              <p className="text-sm text-gray-700 leading-relaxed">{advisor.description}</p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
              <h5 className="font-bold text-blue-900 text-sm mb-2">Essential Experience & Qualifications</h5>
              <ul className="space-y-1">
                {advisor.essentialExperience.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold mt-0.5 text-xs">•</span>
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
              <h5 className="font-bold text-green-900 text-sm mb-2">Key Knowledge Areas</h5>
              <ul className="space-y-1">
                {advisor.knowledgeAreas.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold mt-0.5 text-xs">•</span>
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-purple-50 border-l-4 border-purple-500 p-3 rounded">
              <h5 className="font-bold text-purple-900 text-sm mb-2">Questions to Ask When Evaluating</h5>
              <ul className="space-y-1.5">
                {advisor.questionsToAsk.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <span className="text-purple-600 font-bold mt-0.5 text-xs">?</span>
                    <span className="text-sm text-gray-700 italic">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
              <h5 className="font-bold text-red-900 text-sm mb-2">🚩 Red Flags to Watch For</h5>
              <ul className="space-y-1">
                {advisor.redFlags.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <span className="text-red-600 font-bold mt-0.5 text-xs">⚠</span>
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  const LessCommonAdvisorCard = ({ advisor }) => (
    <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-gray-50 to-slate-50">
      <h4 className="font-bold text-gray-900 text-sm mb-1">{advisor.title}</h4>
      <p className="text-xs text-gray-700 mb-2">{advisor.description}</p>
      <div className="bg-amber-50 border-l-2 border-amber-400 pl-2 py-1">
        <p className="text-xs text-gray-600"><span className="font-semibold">When You Need Them:</span> {advisor.whenNeeded}</p>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
          <div className="flex items-center gap-3">
            <HelpCircle size={28} />
            <div>
              <h2 className="text-2xl font-bold">Advisory Team Guide</h2>
              <p className="text-sm text-teal-50">Essential criteria for selecting your professional advisors</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 space-y-6 flex-1">
          {/* Introduction */}
          <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded">
            <h3 className="font-bold text-teal-900 mb-2">Why Your Advisory Team Matters</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              A strong advisory team is one of the most valuable assets you can build as a business owner planning an exit.
              The right advisors will help you maximize business value, minimize taxes, navigate complex transactions, and achieve
              your personal and financial goals. This guide will help you identify, evaluate, and select advisors who are
              truly qualified to support your exit journey.
            </p>
          </div>

          {/* Common Advisors Section */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Most Common Professional Advisors</h3>
            <p className="text-sm text-gray-600 mb-4">
              Click on each advisor type below to see detailed qualifications, experience requirements, and questions to ask.
            </p>
            <div className="space-y-3">
              {commonAdvisors.map(advisor => (
                <AdvisorCard key={advisor.key} advisor={advisor} />
              ))}
            </div>
          </div>

          {/* Less Common Advisors Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Less Common (But Often Valuable) Advisors</h3>
                <p className="text-sm text-gray-600">Specialists you may need depending on your business, industry, or transaction complexity.</p>
              </div>
              <button
                onClick={() => setShowLessCommon(!showLessCommon)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-sm font-medium"
              >
                {showLessCommon ? 'Hide' : 'Show'}
                {showLessCommon ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
            </div>

            {showLessCommon && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lessCommonAdvisors.map((advisor, idx) => (
                  <LessCommonAdvisorCard key={idx} advisor={advisor} />
                ))}
              </div>
            )}
          </div>

          {/* Best Practices */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
              <span className="text-xl">💡</span>
              Best Practices for Building Your Advisory Team
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold mt-0.5">1.</span>
                <span className="text-sm text-gray-700"><strong>Start early:</strong> Build relationships with advisors 3-5 years before your planned exit, not 3-5 months.</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold mt-0.5">2.</span>
                <span className="text-sm text-gray-700"><strong>Check references:</strong> Always speak with at least 2-3 clients who have completed exits similar to yours.</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold mt-0.5">3.</span>
                <span className="text-sm text-gray-700"><strong>Ensure collaboration:</strong> Your advisors should be willing and able to work together as a coordinated team.</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold mt-0.5">4.</span>
                <span className="text-sm text-gray-700"><strong>Prioritize experience:</strong> Exit planning is not the time for advisors to learn on your dime. Seek proven expertise.</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold mt-0.5">5.</span>
                <span className="text-sm text-gray-700"><strong>Understand fees:</strong> Be clear on how each advisor is compensated and ensure fee structures align with your interests.</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold mt-0.5">6.</span>
                <span className="text-sm text-gray-700"><strong>Trust your instincts:</strong> You should feel confident in your advisors' abilities and comfortable with their communication style.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3">
          <DownloadPDFButton
            type="advisory-guide"
            label="Download PDF Guide"
            variant="secondary"
            className="flex-1"
          />
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition font-semibold min-h-[44px]"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

AdvisoryTeamHelpModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default AdvisoryTeamHelpModal;
